/* @@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@ */
/* @@@@@@@@@@@@@@@@@@@@@@@@@@@@ THIS HOLDS THE INDIVIDUAL COURSE STANDALONE PAGES @@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@ */
/* @@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@ */

import React, { useState } from "react";
import { graphql, Link } from "gatsby";
import Layout from "../components/layout";
import { Container, Row, Col } from "react-bootstrap";
import Seo from "../components/seo";
import { renderRichText } from "gatsby-source-contentful/rich-text";
import { MARKS, INLINES, BLOCKS } from "@contentful/rich-text-types";
import "../styles/course-styles.css";
import moment from "moment";
import ModalVideo from "../components/modalVideo";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarDay,
  faCircleHalfStroke,
  faCalendarCheck,
  faSun,
  faClock,
  faSchool,
  faBarcode,
  faHouse,
  faSquareXmark,
  faDesktop,
  faHouseLaptop,
  faBook,
  faCode,
  faComments,
  faCalculator,
  faFlask,
  faFlaskVial,
  faPenNib,
  faLocationCrosshairs,
  faArrowLeft,
  faCircleExclamation,
  faCircleDot,
  faClipboard,
  faDownload,
} from "@fortawesome/free-solid-svg-icons";

// Helper function to convert military time (24-hour format) to 12-hour format with AM/PM
const convertToAmPm = (time) => {
  // Split the time string into hours and minutes
  const [hours, minutes] = time.split(":").map(Number);

  // Determine whether it's AM or PM
  const ampm = hours >= 12 ? "pm" : "am";

  // Convert to 12-hour format (handle 12 PM and 12 AM correctly)
  const adjustedHours = hours % 12 || 12;

  // Return the formatted time with AM/PM
  return `${adjustedHours}:${minutes.toString().padStart(2, "0")} ${ampm}`;
};

const CourseTemplate = ({ data }) => {
  const courseContent = data.contentfulSuCourse;
  const courseGroup = data.contentfulSuCourse.courseType;
  const pageContent = data.contentfulSuPage || {};
  const { nodes: pages = [] } = data.allContentfulSuPage || {};
  const { nodes: suContents = [] } = data.allContentfulSuContent || {};

  // *********************************** FUNCTION TO BUILD URL PATHS ***********************************
  function getEntryWithId(pageId) {
    const matchingIdSuPage = data.allContentfulSuPage.nodes.find(
      (node) => node.contentful_id === pageId
    );

    const matchingIdPdf = data.allContentfulPdf.nodes.find(
      (node) => node.contentful_id === pageId
    );

    const matchingIdSuCourse = data.allContentfulSuCourse.nodes.find(
      (node) => node.contentful_id === pageId
    );

    const matchingId = matchingIdPdf || matchingIdSuPage || matchingIdSuCourse;

    if (!matchingId) return;
    const matchingEntry =
      matchingId.pageSlug || matchingId.pdfSlug || matchingId.courseSlug;
    const matchingType = matchingId.__typename;

    const buildUrlPath = (items, slug, type) => {
      if (type === "ContentfulPdf") {
        return `${slug}.pdf`;
      }

      if (type === "ContentfulSuCourse") {
        return `courses/${slug}`;
      }

      for (const item of items) {
        if (item.navSlug === slug) {
          return `${item.navSlug}`;
        }
        if (item.navNextLevel) {
          const nextLevelPath = buildUrlPath(item.navNextLevel, slug, type);
          if (nextLevelPath !== "") {
            return `${item.navSlug}/${nextLevelPath}`;
          }
        }
      }
      return "";
    };

    //const urlPath = buildUrlPath(data.allContentfulCrNavGroup.nodes, matchingEntry, matchingType);
    const urlPath = buildUrlPath(
      data.allContentfulSuNavGroup.nodes,
      matchingEntry,
      matchingType
    );
    return `/${urlPath}`;
  }

  // ***************************** OPTIONS FOR RICH TEXT RENDERING *****************************
  const options = {
    renderText: (text) => {
      return text.split("\n").reduce((children, textSegment, index) => {
        return [...children, index > 0 && <br key={index} />, textSegment];
      }, []);
    },
    renderMark: {
      [MARKS.BOLD]: (text) => <strong className="font-bold">{text}</strong>,
    },
    renderNode: {
      [INLINES.ENTRY_HYPERLINK]: (node, children) => {
        const referencedEntry = getEntryWithId(node.data.target.sys.id);
        if (!referencedEntry) return children;
        const isPdf = referencedEntry?.endsWith(".pdf");
        return isPdf ? (
          <a href={`${referencedEntry}`} target="_blank" rel="noreferrer">
            {children}
          </a>
        ) : (
          <Link to={`${referencedEntry}`}>{children}</Link>
        );
      },
      [INLINES.HYPERLINK]: (node, children) => {
        const referencedEntry = node.data.uri;
        return (
          <Link to={`${referencedEntry}`} target="_blank">
            {children}
          </Link>
        );
      },
      [BLOCKS.HEADING_2]: (node, children) => <h2>{children}</h2>,
      [BLOCKS.TABLE]: (node, children) => (
        <table className="other-table">{children}</table>
      ),
    },
  };

  return (
    <Layout courseGroup={courseGroup}>
      <Seo title={courseContent.courseTitle} />

      <Container className="course-hero" fluid>
        <Container id="hero-image">
          <img
            src={
              courseContent?.courseImage?.url ||
              "https://images.ctfassets.net/evvt5b3xxl3c/63K0pRclFjuIpZL7DHuhIY/0aeb6c9918825a41307be865219f4e7c/general-header.webp"
            }
            alt={courseContent?.courseTitle || "Default Course Title"}
          />
        </Container>
        <Container id="course-titles">
          <Row>
            <Col xs={9} lg={8}>
              <div className="whitegradient"></div>
              {courseContent?.courseTitle && (
                <>
                  <h1>{courseContent.courseTitle}</h1>
                  <br />
                  {/* ************************************* BACK TO FILTER BUTTON ************************************* */}
                  <button
                    className="course-button"
                    onClick={() => {
                      const referrer = document.referrer; // Get the previous page URL

                      // Check if the previous page matches the desired paths
                      const isPreCollegeCourseFinder = referrer.includes(
                        "/pre-college/pre-college-course-finder/"
                      );
                      const isUndergraduateCourseFinder = referrer.includes(
                        "/undergraduates/undergraduate-course-finder/"
                      );

                      if (
                        isPreCollegeCourseFinder ||
                        isUndergraduateCourseFinder
                      ) {
                        // Use the browser's back function to return to the previous page
                        window.history.back();
                      } else {
                        // Handle fallback based on courseContent.courseType
                        let redirectUrl = "/"; // Default to home page

                        if (courseContent.courseType === "Pre-College") {
                          redirectUrl =
                            "/pre-college/pre-college-course-finder/";
                        } else if (
                          courseContent.courseType === "Undergraduate"
                        ) {
                          redirectUrl =
                            "/undergraduates/undergraduate-course-finder/";
                        } else if (courseContent.courseType === "Both") {
                          redirectUrl = "/"; // Both redirects to the homepage
                        }

                        window.location.href = redirectUrl; // Redirect to the determined URL
                      }
                    }}
                  >
                    <svg className="svgCourseIcon" viewBox="0 0 384 512">
                      <FontAwesomeIcon icon={faArrowLeft} />
                    </svg>
                  </button>
                </>
              )}
            </Col>
          </Row>
        </Container>
      </Container>
      <Container fluid className="course-details">
        <Container id={courseContent.styleId}>
          <Row>
            <Col className="course-body" xs={12} md={8}>
              {/* ************************************* COURSE TYPE - TESTING PURPOSE ************************************* */}
              {/* {courseContent?.courseType && <h3>*TESTING* courseType = {courseContent.courseType}</h3>} */}

              {/* ************************************* COURSE FULL ************************************* */}
              {courseContent?.courseFull === "Course FULL" && (
                <p className="class-full">
                  {/* <FontAwesomeIcon icon={faSquareXmark} /> */}
                  <i class="fas fa-square-xmark"></i>
                  Course is Full
                </p>
              )}

              {/* ************************************* COURSE CODE ************************************* */}
              {courseContent?.courseCode &&
                courseContent.courseType !== "Pre-College" && (
                  <>
                    <h2>Course Code</h2>
                    <p>{courseContent.courseCode}</p>
                  </>
                )}


              {/* ************************************* COURSE CODE ************************************* */}
              {courseContent?.crossListedCourseCode &&
                courseContent.courseType !== "Pre-College" && (
                  <>
                    <h2>Cross Listed Course Code(s)</h2>
                    <p>{courseContent.crossListedCourseCode}</p>
                  </>
                )}



              {/* ************************************* COURSE DESCRIPTION ************************************* */}
              {courseContent?.courseDescription && (
                <>
                  <h2>Course Description</h2>
                  {renderRichText(courseContent.courseDescription)}
                </>
              )}

              {/* ************************************* COURSE CRITERIA ************************************* */}
              {courseContent?.courseCriteria && (
                <>
                  <h2>Course Criteria</h2>
                  <div className="custom-course-text">
                    {renderRichText(courseContent.courseCriteria)}
                  </div>
                </>
              )}

              {/* ************************************* COURSE CRITERIA CHECKBOX ************************************* */}
              {(courseContent?.courseCriteriaCheckbox ===
                "Show Course Criteria Below" &&
                courseContent.courseType === "Pre-College") ||
                (courseContent.courseType === "Both" && (
                  <>
                    {!courseContent?.courseCriteria && <h2>Course Criteria</h2>}
                    <p className="preset-course-text">
                      This course is primarily comprised of undergraduate
                      students. A select number of places are reserved for
                      advanced high school students.
                    </p>

                    {courseContent?.costs && (
                      <>
                        <p className="preset-course-text">
                          The cost of this course for pre-college students is{" "}
                          <span>
                            {courseContent.costs ===
                              "100 – 1 Week Enrichment" && "$2,500"}
                            {courseContent.costs ===
                              "200 – 2 Week Career Insight" && "$5,625"}
                            {courseContent.costs === "300 – 3 Week Immersion" &&
                              "$8,900"}
                            {courseContent.costs === "400 – 4 Week Practicum" &&
                              "$14,490"}
                            {courseContent.costs ===
                              "500 – Summer Online/College/VS/UChicago" &&
                              "$4,790"}
                            {courseContent.costs ===
                              "600 – Summer Language Institute (300)" &&
                              "$8,225"}
                          </span>
                        </p>
                      </>
                    )}
                  </>
                ))}

              {/* ************************************* INTENSIVE NOTICE ************************************* */}
              {courseContent?.intensiveNotice?.length > 0 &&
                courseContent.courseType === "Pre-College" && (
                  <>
                    <p className="intensive">
                      {courseContent.intensiveNotice.includes(
                        "Writing Intensive"
                      ) && (
                          <div className="intensive-wrapper">
                            <span>
                              {/* <FontAwesomeIcon icon={faPenNib} />  */}
                              <i class="fas fa-pen-nib"></i>
                              Writing
                              Intensive{" "}
                            </span>
                            <div className="hover-message">
                              Students will be required to write at least 3 essays
                              or papers during the duration of the course.
                            </div>
                          </div>
                        )}
                      {courseContent.intensiveNotice.includes("Lab") && (
                        <div className="intensive-wrapper">
                          <span>
                            {/* <FontAwesomeIcon icon={faFlask} /> */}
                            <i class="fas fa-flask"></i> Lab Intensive
                            <br />{" "}
                          </span>
                          <div className="hover-message">
                            The majority of class time will be spent in the lab.
                          </div>
                        </div>
                      )}
                      {courseContent.intensiveNotice.includes(
                        "Math Intensive"
                      ) && (
                          <div className="intensive-wrapper">
                            <span>
                              {/* <FontAwesomeIcon icon={faCalculator} /> */}
                              <i class="fas fa-calculator"></i> Math
                              Intensive
                            </span>
                            <div className="hover-message">
                              Students should expect daily class and homework
                              activities to involve coding.
                            </div>
                          </div>
                        )}
                      {courseContent.intensiveNotice.includes(
                        "Discussion Intensive"
                      ) && (
                          <div className="intensive-wrapper">
                            <span>
                              {/* <FontAwesomeIcon icon={faComments} /> */}
                              <i class="fas fa-comments"></i> Discussion
                              Intensive
                            </span>
                            <div className="hover-message">
                              The majority of class time will be devoted to
                              seminar-style learning.
                            </div>
                          </div>
                        )}
                      {courseContent.intensiveNotice.includes(
                        "Reading Intensive"
                      ) && (
                          <div className="intensive-wrapper">
                            <span>
                              {/* <FontAwesomeIcon icon={faBook} /> */}
                              <i class="fas fa-book"></i> Reading Intensive{" "}
                            </span>
                            <div className="hover-message">
                              Students should expect to read at least 30 pages per
                              night.
                            </div>
                          </div>
                        )}
                      {courseContent.intensiveNotice.includes(
                        "Coding Intensive"
                      ) && (
                          <div className="intensive-wrapper">
                            <span>
                              {/* <FontAwesomeIcon icon={faCode} /> */}
                              <i class="fas fa-code"></i> Coding Intensive
                            </span>
                            <div className="hover-message">
                              Students should expect daily class and homework
                              activities to involve coding.
                            </div>
                          </div>
                        )}
                    </p>
                  </>
                )}

              {/* ************************************* ACADEMIC INTEREST ************************************* */}
              {courseContent?.academicInterest &&
                courseContent.courseType === "Pre-College" && (
                  <>
                    <h2>Academic Interest</h2>
                    <p>
                      {courseContent.academicInterest.map((interest, index) => (
                        <span key={index}>
                          {interest}
                          {index < courseContent.academicInterest.length - 1
                            ? ", "
                            : ""}
                        </span>
                      ))}
                    </p>
                  </>
                )}

              {/* ************************************* APPLICATION MATERIALS ************************************* */}

              {(courseContent?.applicationMaterials ||
                courseContent?.additionalAppMaterials) &&
                courseContent.courseType === "Pre-College" && (
                  <>
                    <h2>Application Materials</h2>
                    <p>
                      {courseContent.applicationMaterials?.map(
                        (material, index) => {
                          let displayContent;

                          switch (material) {
                            case "Material A":
                              displayContent = (
                                <>
                                  A complete application includes a transcript,
                                  two short essays, a letter of recommendation,
                                  writing sample, application fee, and a
                                  submitted parent confirmation. If you are
                                  seeking need-based financial aid, you must
                                  indicate that in your application before it is
                                  submitted. Please refer to the{" "}
                                  <a
                                    href="/pre-college/application-instructions/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    Application Instructions
                                  </a>{" "}
                                  for complete details.
                                </>
                              );
                              break;
                            case "Material B":
                              displayContent = (
                                <>
                                  Applicants to 1 Week Enrichment may have a
                                  counselor or teacher{" "}
                                  <a
                                    href="https://applypcs.uchicago.edu/register/?id=b4d7f560-80e1-4ebf-a8e3-adef80c447f6"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    submit a nomination
                                  </a>{" "}
                                  in addition to the required letter of
                                  recommendation.
                                </>
                              );
                              break;
                            case "Material C":
                              displayContent =
                                "A complete application includes a transcript, proof of English proficiency, and an application fee. Please refer to the Application Instructions for complete details.";
                              break;
                            default:
                              displayContent = null; // Skip if none of the cases match
                          }

                          return displayContent ? (
                            <span key={index}>
                              {displayContent}
                              {index <
                                courseContent.applicationMaterials.length - 1 &&
                                " "}
                            </span>
                          ) : null;
                        }
                      )}
                    </p>

                    {/* ************************************* ADDITIONAL MATERIALS  ************************************* */}

                    {courseContent.additionalAppMaterials && (
                      <>
                        <strong>Additional Materials:</strong>{" "}
                        {renderRichText(
                          courseContent.additionalAppMaterials,
                          options
                        )}
                      </>
                    )}
                  </>
                )}

              {/* ************************************* INSTRUCTORS  ************************************* */}
              {(courseContent?.instructor ||
                courseContent?.additionalInstructors) && (
                  <>
                    <h2>Instructor(s)</h2>
                    <p>
                      {courseContent.instructor}
                      {courseContent.additionalInstructors &&
                        `, ${courseContent.additionalInstructors}`}
                    </p>
                  </>
                )}

              {/* ************************************* COSTS ************************************* */}
              {courseContent?.costs &&
                courseContent.courseType === "Pre-College" && (
                  <>
                    <h2>Cost</h2>
                    <p>
                      {courseContent.costs === "100 – 1 Week Enrichment" &&
                        "$2,500"}
                      {courseContent.costs === "200 – 2 Week Career Insight" &&
                        "$5,625"}
                      {courseContent.costs === "300 – 3 Week Immersion" &&
                        "$8,900"}
                      {courseContent.costs === "400 – 4 Week Practicum" &&
                        "$14,490"}
                      {courseContent.costs ===
                        "500 – Summer Online/College/VS/UChicago" && "$4,790"}
                      {courseContent.costs ===
                        "600 – Summer Language Institute (300)" && "$8,225"}

                      {courseContent.costs === "Custom Cost" &&
                        courseContent.customCost}

                      {(courseContent.costs || courseContent.customCost) && (
                        <div className="twelfth-wrapper">
                          <span>
                            {/* <FontAwesomeIcon icon={faCircleExclamation} /> */}
                            <i class="fas fa-cirlce-exclamation"></i>
                          </span>
                          <div className="hover-message-filter">
                            Need-based financial aid is available. Students
                            should refer the Costs & Aid page and apply for aid
                            when they submit their application to Summer
                            Session.
                          </div>
                        </div>
                      )}
                    </p>
                  </>
                )}

              {/* ************************************* COST NOTE  ************************************* */}
              {((courseContent?.courseType === "Pre-College" || courseContent?.courseType === "Both") &&
                courseContent?.costNotes?.includes("Enrichment Cost Note")) && (
                  <>
                    <p className="cost-notes">
                      Significant financial support is available for these
                      programs, and any student can apply for aid. In addition,
                      students applying only to 1 Week Enrichment programs, and
                      whose household income is $120,000 or below, are eligible
                      for the following financial support, as available:
                      <ul>
                        <li>
                          <strong>
                            Household income at or below $120,000 USD:
                          </strong>{" "}
                          Program fee is waived (e.g., no cost to the student,
                          exclusive of travel expenses).
                        </li>
                        <li>
                          <strong>
                            Household income at or below $65,000 USD:
                          </strong>{" "}
                          Program fee is waived + domestic roundtrip travel
                          expenses covered (e.g., no cost to the student,
                          inclusive of travel expenses for domestic students).
                        </li>
                      </ul>
                    </p>
                  </>
                )}

              {((courseContent?.courseType === "Pre-College" || courseContent?.courseType === "Both") &&
                courseContent?.costNotes?.includes("EWL Cost Note")) && (
                  <>
                    <p className="cost-notes">
                      Significant financial support is available for Emerging World Leaders, including roundtrip travel expenses. If the student's household income is $120,000 USD or below, they are eligible for the following financial support, as available:
                      <ul>
                        <li>
                          <strong>
                            Household income at or below $120,000 USD:
                          </strong>{" "}
                          Program fee is waived (e.g., no cost to the student, exclusive of travel expenses).
                        </li>
                        <li>
                          <strong>
                            Household income at or below $65,000 USD:
                          </strong>{" "}
                          Program fee is waived + roundtrip travel expenses covered (e.g., no cost to the student, inclusive of travel expenses for students). Emerging World Leaders is the only program that provides funding for travel expenses for students who attend high school outside of the U.S.
                        </li>
                      </ul>
                    </p>
                  </>
                )}

              {/* ************************************* COURSE FULL  ************************************* */}
              {courseContent.courseType === "Pre-College" &&
                (courseContent.courseFull === "Course FULL" ? (
                  <p className="text-grey">
                    This course is completely full. Please choose another course instead. We recommend that you apply for more than one course on your application.
                  </p>
                ) : ( null
                  // <a
                  //   href="/pre-college/application-instructions/"
                  //   className="btn"
                  // >
                  //   Start Your Application
                  // </a>
                ))}

              {courseContent.courseType === "Undergraduate" &&
                (courseContent.courseFull === "Course FULL" ? (

                  <p className="text-grey">
                    This course is full. UChicago students can request to be added on a{" "}
                    <a href="https://docs.google.com/forms/d/e/1FAIpQLSexQ3J9sY60mP5WhQy2ogWCRNgWpMrdIXFx-WtiL9FFaHd9TA/viewform" target="_blank" rel="noopener noreferrer" className="text-blue-500 underline">
                      waitlist
                    </a>.
                    Visiting students are strongly invited to choose another course and email{" "}
                    <a href="mailto:summersession@uchicago.edu" className="text-blue-500 underline">
                      summersession@uchicago.edu
                    </a> to inquire about the waitlist status.
                  </p>
                ) : (
                  <>
                    <a
                      href="/undergraduates/undergrad-student-registration/current-uchicago-students/"
                      className="btn"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      UChicago Registration <sup>1</sup>
                    </a>
                    {/* <a
                      href="https://applypcs.uchicago.edu/apply/"
                      className="btn"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Visiting Application <sup>2</sup>
                    </a> */}
                    <p className="button-instructions">
                      {" "}
                      <sup>1</sup> UChicago students can self-register.{" "}
                    </p>
                    <p className="button-instructions2">
                      {" "}
                      <sup>2</sup> Visiting students and pre-college students
                      apply through the same application.
                    </p>
                  </>
                ))}

              {courseContent.courseType === "Both" &&
                (courseContent.courseFull === "Course FULL" ? (
                  <p className="text-grey">
                    This course is completely full. Please choose another course instead. We recommend that you apply for more than one course on your application.
                  </p>
                ) : (
                  <div>
                    {/* <a
                      href="https://applypcs.uchicago.edu/apply/"
                      className="btn"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Application <sup>1</sup>
                    </a> */}
                    <a
                      href="/undergraduates/undergrad-student-registration/current-uchicago-students/"
                      className="btn"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      UChicago Registration <sup>2</sup>
                    </a>
                    <p className="button-instructions">
                      {" "}
                      <sup>1</sup> Pre-college and visiting students need to
                      apply via the same application form.
                    </p>
                    <p className="button-instructions2">
                      {" "}
                      <sup>2</sup> UChicago students can self-register.{" "}
                    </p>
                  </div>
                ))}
            </Col>

            {/* ****************************************************************************************** */}
            {/* ************************************* COURSE SIDEBAR ************************************* */}
            {/* ****************************************************************************************** */}

            <Col xs={12} md={4}>
              <Col xs={12} className="course-sidebar">
                {/************************************** COURSE DURATION **************************************/}
                {courseContent?.duration && (
                  <>
                    <h4>Course Duration</h4>
                    <p>
                      <i class="fas fa-calendar-day"></i>
                      {/* <FontAwesomeIcon icon={faCalendarDay} />{" "} */}
                      {courseContent.duration}
                    </p>
                  </>
                )}

                {/************************************** SESSION **************************************/}
                {courseContent?.startOfSession && (
                  <>
                    <h4>Session</h4>
                    <p>
                      <i class="fas fa-circle-half-stroke"></i>
                      {/* <FontAwesomeIcon icon={faCircleHalfStroke} /> */}{" "}
                      {courseContent.startOfSession}
                    </p>
                  </>
                )}

                {/*********************************** COURSE START / END DATE ***********************************/}
                {(courseContent?.courseStartDate ||
                  courseContent?.courseEndDate) && (
                    <>
                      <h4>Course Dates</h4>
                      <p>
                        <i class="fas fa-calendar-check"></i>

                        {/* <FontAwesomeIcon icon={faCalendarCheck} /> */}{" "}
                        {courseContent?.courseStartDate &&
                          `${moment(courseContent.courseStartDate).format(
                            "MMMM Do"
                          )}`}
                        {courseContent?.courseEndDate &&
                          ` - ${moment(courseContent.courseEndDate).format(
                            "MMMM Do"
                          )}`}
                      </p>
                    </>
                  )}

                {/************************************** CLASS DAYS **************************************/}
                {Array.isArray(courseContent?.classDays) && (
                  <>
                    <h4>Class Days</h4>
                    <p>
                      {/* <FontAwesomeIcon icon={faSun} /> */}
                      <i class="fas fa-sun"></i>{" "}
                      {courseContent.classDays
                        .sort((a, b) => {
                          const daysOrder = [
                            "Sun",
                            "Mon",
                            "Tue",
                            "Wed",
                            "Thu",
                            "Fri",
                            "Sat",
                          ];
                          return (
                            daysOrder.indexOf(a.slice(0, 3)) -
                            daysOrder.indexOf(b.slice(0, 3))
                          );
                        })
                        .map((day, index) => (
                          <span key={index}>
                            {day.slice(0, 3)}
                            {index < courseContent.classDays.length - 1 && ", "}
                          </span>
                        ))}
                    </p>
                  </>
                )}

                {/*********************************** CLASS START / END DATE ***********************************/}
                {(courseContent?.classStartTime ||
                  courseContent?.classEndTime) && (
                    <>
                      <h4>Class Time</h4>
                      <p>
                        {/* <FontAwesomeIcon icon={faClock} /> */}
                        <i class="fas fa-clock"></i>
                        {convertToAmPm(courseContent.classStartTime)}
                        {" - "}
                        {convertToAmPm(courseContent.classEndTime)}
                      </p>
                    </>
                  )}

                {/************************************** LAB DAYS **************************************/}
                {Array.isArray(courseContent?.labDays) && (
                  <>
                    <h4>Lab Days</h4>
                    <p>
                      {/* <FontAwesomeIcon icon={faFlask} /> */}
                      <i class="fas fa-flask"></i>{" "}
                      {courseContent.labDays
                        .sort((a, b) => {
                          const dayOrder = [
                            "Monday",
                            "Tuesday",
                            "Wednesday",
                            "Thursday",
                            "Friday",
                          ];
                          return dayOrder.indexOf(a) - dayOrder.indexOf(b);
                        })
                        .map((day, index) => (
                          <span key={index}>
                            {day.slice(0, 3)}
                            {index < courseContent.labDays.length - 1 && ", "}
                          </span>
                        ))}
                    </p>
                  </>
                )}

                {/************************************** LAB START / END TIME ********************************/}
                {(courseContent?.labStartTime || courseContent?.labEndTime) && (
                  <>
                    <h4>Lab Time</h4>
                    <p>
                      {/* <FontAwesomeIcon icon={faFlaskVial} /> */}
                      <i class="fas fa-flask-vial"></i>
                      {convertToAmPm(courseContent.labStartTime)}
                      {" - "}
                      {convertToAmPm(courseContent.labEndTime)}
                    </p>
                  </>
                )}

                {/************************************** CURRENT GRADE **************************************/}
                {courseContent?.currentGrade && (
                  <>
                    <h4>Eligibility</h4>
                    <p>
                      {/* <FontAwesomeIcon icon={faSchool} /> */}
                      <i class="fas fa-school"></i>{" "}
                      {[
                        ...courseContent.currentGrade,
                        ...(courseContent.courseType === "Both"
                          ? ["Undergrad"]
                          : []),
                      ]
                        .sort((a, b) => {
                          // Handle numerical sorting for grades, keeping "Undergrad" at the end
                          if (a === "Undergrad") return 1;
                          if (b === "Undergrad") return -1;
                          return parseInt(a) - parseInt(b);
                        })
                        .map((grade, index, array) => (
                          <span key={index}>
                            {grade}
                            {index < array.length - 1 ? ", " : ""}
                          </span>
                        ))}
                    </p>
                  </>
                )}

                {/* ************************************* CORE COURSE ************************************* */}
                {courseContent?.coreCourse &&
                  courseContent.courseType !== "Pre-College" && (
                    <>
                      <h4>Core Course</h4>
                      <p>
                        {/* <FontAwesomeIcon icon={faCircleDot} /> */}
                        <i class="fas fa-circle-dot"></i>
                        Part of UChicago Core Curriculum
                      </p>
                    </>
                  )}

                {/************************************** COURSE CODE **************************************/}
                {courseContent?.courseCode &&
                  courseContent.courseType !== "Pre-College" && (
                    <>
                      <h4>Course Code</h4>
                      <p>
                        {/* <FontAwesomeIcon icon={faBarcode} /> */}
                        <i class="fas fa-barcode"></i>{" "}
                        {courseContent.courseCode}
                      </p>
                    </>
                  )}

                {/************************************** MODALITY **************************************/}
                {courseContent?.remoteOrCampus && (
                  <>
                    <h4>Modality</h4>
                    <p>
                      {/* <FontAwesomeIcon
                        icon={
                          courseContent.remoteOrCampus === "Residential"
                            ? faHouse
                            : courseContent.remoteOrCampus === "Remote"
                              ? faDesktop
                              : faHouseLaptop
                        }
                      />{" "}
                      {courseContent.remoteOrCampus === "Residential" &&
                        (courseContent.courseType === "Undergraduate" ||
                          courseContent.courseType === "Both")
                        ? "In-Person"
                        : courseContent.remoteOrCampus} */}
                      <p>
                        <i
                          className={`fas ${courseContent.remoteOrCampus === "Residential"
                            ? "fa-house"
                            : courseContent.remoteOrCampus === "Remote"
                              ? "fa-desktop"
                              : "fa-house-laptop"
                            }`}
                        ></i>
                        {courseContent.remoteOrCampus === "Residential" &&
                          (courseContent.courseType === "Undergraduate" ||
                            courseContent.courseType === "Both")
                          ? "In-Person"
                          : courseContent.remoteOrCampus}
                      </p>
                    </p>
                  </>
                )}
                {/************************************** SYLLABUS **************************************/}
                {courseContent?.syllabusPdf ? (
                  <>
                    <h4>Syllabus</h4>
                    <p>
                      {/* <FontAwesomeIcon icon={faDownload} /> */}
                      <i class="fas fa-download"></i>{" "}
                      <a
                        href={`/${courseContent.syllabusPdf.pdfSlug}.pdf`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Preview Sample Syllabus
                      </a>
                    </p>
                  </>
                ) : null}
              </Col>
              {/************************************** COURSE VIDEO **************************************/}
              {courseContent.courseVideo &&
                courseContent.courseVideo.videoId ? (
                <Col xs={12} className="video-sidebar">
                  <ModalVideo
                    id={courseContent.courseVideo.id}
                    title={
                      courseContent.courseVideo.videoName || "Course Video"
                    }
                    youtubeID={courseContent.courseVideo.videoId}
                    caption={courseContent.courseVideo.videoCaption}
                    videoImageThumbnail={
                      courseContent.courseVideo.videoImageThumbnail
                    }
                  />
                </Col>
              ) : null}
            </Col>
          </Row>
        </Container>
      </Container>
      {/************************************** SIMILAR COURSES **************************************/}
      {courseContent.similarCoursesToConsider &&
        courseContent.similarCoursesToConsider.length > 0 && (
          <Container fluid className="similar-courses">
            <Container>
              <Row>
                <Col>
                  <h4>Other Courses to Consider</h4>
                  <p>These courses might also be of interest.</p>
                </Col>
              </Row>
              <Row>
                <Col>
                  {/* ******************************* ADDITIONAL COURSES ***************************** */}
                  <ul className="similar-course-list">
                    {courseContent.similarCoursesToConsider.map(
                      (course, index) => (
                        <li key={index} className="similar-course-item">
                          <a
                            href={`/courses/${course.courseSlug}`}
                            className="similar-course-link"
                          >
                            {course.courseImage && course.courseImage.url && (
                              <div>
                                <img
                                  className="additional-img"
                                  src={course.courseImage.url}
                                  alt={course.courseTitle}
                                />
                              </div>
                            )}
                            <div className="similar-course-title">
                              {course.courseTitle}
                            </div>
                          </a>
                          <div className="course-description">
                            {course.courseDescription && (
                              <>
                                <div className="description-text">
                                  {renderRichText(course.courseDescription)}
                                </div>
                              </>
                            )}
                          </div>
                          <div className="remote-campus">
                            {course.remoteOrCampus}
                          </div>
                        </li>
                      )
                    )}
                  </ul>
                </Col>
              </Row>
            </Container>
          </Container>
        )}
    </Layout>
  );
};

export const query = graphql`
  query($id: String!) {
    contentfulSuCourse(id: { eq: $id }) {
      contentful_id
      id
      styleId
      courseSlug
      courseType
      coreCourse
      applicationMaterials
      additionalAppMaterials {
        raw
      }

      courseCriteriaCheckbox
      courseImage {
        url
      }
      courseTitle
      courseDescription {
        raw
      }
      courseCriteria {
        raw
      }
      duration
      startOfSession
      currentGrade
      remoteOrCampus
      academicInterest
      courseStartDate
      courseEndDate
      courseFull
      syllabusPdf {
        pdfSlug
      }
      courseCode
      crossListedCourseCode
      classDays
      classStartTime
      classEndTime
      instructor
      additionalInstructors
      costs
      costNotes
      customCost
      intensiveNotice
      labDays
      labStartTime
      labEndTime
      courseVideo {
        id
        styleId
        videoName
        videoId
        videoCaption {
          raw
        }
        videoImageThumbnail {
          gatsbyImageData(width: 500, layout: CONSTRAINED)
        }
      }
      similarCoursesToConsider {
        courseTitle
        courseSlug
        courseImage {
          url
        }
        courseDescription {
          raw
        }
        remoteOrCampus
      }
    }
    allContentfulSuPage {
      nodes {
        pageSlug
        id
        contentful_id
      }
    }
    allContentfulSuCourse {
      nodes {
        id
        styleId
        courseSlug
        contentful_id
        __typename
      }
    }
    allContentfulPdf(filter: { site: { eq: "Summer" } }) {
      nodes {
        documentTitle
        id
        contentful_id
        pdfSlug
        site
        __typename
        pdfDocument {
          description
          file {
            fileName
            url
          }
        }
      }
    }
    allContentfulSuNavItem {
      nodes {
        navSlug
        navItemName
        navNextLevel {
          navSlug
          navNextLevel {
            navSlug
            navNextLevel {
              navSlug
              navNextLevel {
                navSlug
              }
            }
          }
        }
      }
    }
    allContentfulSuNavGroup {
      nodes {
        navGroupName
        navSlug
        contentful_id
        navNextLevel {
          navSlug
          navItemName
          contentful_id
          navNextLevel {
            navSlug
            contentful_id
            navNextLevel {
              navSlug
              contentful_id
            }
          }
        }
      }
    }
  }
`;

export default CourseTemplate;
